import React from 'react';
import { ReleaseService } from '@/api/release';
import { Loading } from '@/components/Loading';
import { Section } from '@/components/Section';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { Chart } from 'primereact/chart';
import { useParams } from 'react-router-dom';
import { StatusColor, StatusLabels } from '@/utils/constants/testRun';
import { MdOutlineSmartDisplay } from 'react-icons/md';
import { StackedProgressBar } from '@/components/shared/StackedProgressBar';
import { Button } from 'alurkerja-ui';
import { formatFullDate } from '@/utils/libs/dateUtil';
import { PieChartLabel } from '@/components/release/PieChartLabel';
import { FaShare } from 'react-icons/fa';
import _ from 'lodash';
import { ReleaseContent } from './ReleaseContent';
import { useBackNavigate } from '@/utils/hooks/useBackNavigate';

type DetailReleaseProps = {
  isPublic?: boolean;
};

export const DetailRelease: React.FC<DetailReleaseProps> = ({ isPublic }) => {
  const { releaseId } = useParams();
  const backNavigate = useBackNavigate();

  const { data, isFetching } = ReleaseService.useGet(releaseId);

  // filter status with total > 0
  const rawLabels = Object.entries(data?.report?.status ?? {})
    .filter(([, value]) => value.total > 0)
    .map(([status]) => status);

  // map status to labels
  const labels = rawLabels.map(label => StatusLabels[label]);
  const chartDatasets = rawLabels.map(label =>
    _.get(data?.report?.status, `${label}.total`, 0),
  );

  // map status to progress
  const progressDatasets = rawLabels.map(label => ({
    value: _.get(data?.report?.status, `${label}.percentage`, 0) * 100,
    color: StatusColor[label],
  }));
  const progressTotal = rawLabels
    .filter(e => e !== 'untested')
    .reduce(
      (acc, curr) =>
        acc + _.get(data?.report?.status, `${curr}.percentage`, 0) * 100,
      0,
    );

  const chartData = {
    labels,
    datasets: [
      {
        data: chartDatasets,
        backgroundColor: rawLabels.map(label => StatusColor[label]),
      },
    ],
  };

  const chartOptions = {
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  const toShareable = () =>
    window.open(`/public/shareable/release/${releaseId}`, '_blank');

  if (isFetching) {
    return (
      <Section>
        <Loading />
      </Section>
    );
  }

  return (
    <div className="tab-group">
      <Section
        title={`Release - ${data?.name}`}
        actions={
          !isPublic ? (
            <OutlineButton icon={<FaShare />} onClick={toShareable}>
              Shareable Release View
            </OutlineButton>
          ) : undefined
        }
      >
        <div className="flex flex-col gap-6">
          <ReleaseContent title={data?.name}>
            <div className="flex flex-row items-center justify-between px-20 py-10">
              <Chart type="pie" data={chartData} options={chartOptions} />

              <div className="flex flex-col">
                <span className="text-lg">
                  Total Test Cases: {data?.report?.total_test_case}
                </span>

                <div className="mt-2">
                  <PieChartLabel rawLabels={rawLabels} report={data?.report} />
                </div>
              </div>

              <div className="flex flex-col items-center text-2xl font-semibold">
                <span>
                  {((data?.report?.passed_percentage ?? 0) * 100).toFixed(0)}%
                </span>
                <span>Passed</span>
              </div>
            </div>
          </ReleaseContent>

          <ReleaseContent title="Test Runs">
            <div className="flex flex-row items-center justify-between p-6 m-5 bg-white shadow-md">
              <div className="flex flex-row gap-2">
                <div className="flex items-center justify-center w-6 h-6 bg-green-500 rounded-full">
                  <MdOutlineSmartDisplay className="w-3 h-3 text-white" />
                </div>

                <span className="font-semibold">{data?.test_run?.title}</span>
              </div>

              <div className="flex flex-col gap-2">
                <StackedProgressBar max={30} values={progressDatasets} />

                <span className="text-sm font-semibold">
                  {progressTotal.toFixed(0)}%
                </span>
              </div>

              <div className="text-xs w-44">
                {data?.created_by?.full_name} on{' '}
                {formatFullDate(data?.created_at)}
              </div>

              <Button>View Issues</Button>
            </div>
          </ReleaseContent>
        </div>
      </Section>

      {!isPublic && (
        <div className="tab-list">
          <OutlineButton onClick={backNavigate}>Back</OutlineButton>
        </div>
      )}
    </div>
  );
};
